import React, { useState } from 'react';
import PropTypes from 'prop-types';

import CanPerform from 'components/shared/CanPerform';
import { GENERATE_QR_CODES } from 'constants/afterSalesShortcuts';
import { bookingShape, historyShape, userShape } from 'constants/propTypes';

import { useLazyQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import QRCode from 'react-qr-code';

import { isBookingMultiJourney, isUserStationAgent } from 'utils/booking';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import Modal from 'components/ui/Modal';
import { ButtonWrapper, CtaButton, ModalContents } from '../AfterSalesCtas.styles';
import {
  runAfterSaleValidator,
  allSegmentsCanBeRebooked,
  userCanOverrideAfterSalesRules,
  allSegmentsOfService,
  bookingIsConfirmed,
  allSelectedProductsConfirmedCheckedInOrPRRRefunded
} from '../AfterSalesCtas.utils';

import {
  AppleImg,
  AndroidImg,
  ButtonContainer,
  ImgContainer,
  NavigationButton,
  CentredLabelContainer,
  CentredLabelContainerBold
} from './GenerateQRCodesCtas.styles';
import { getBookingTravelDocumentsURL } from '../../../../../utils/urls';
import { ConfirmButton } from '../../../../../components/ui/Button';
import getTickets from 'pages/BookingDetails/GenerateTickets/queries/getTickets';

const PASSBOOK_IOS = 'PASSBOOK';
const PASSBOOK_ANDROID = 'GOOGLE_PAY';

const GenerateQRCodesCta = ({ label, disabled, booking, selectedItemRefs, user, isPassportRequired, history }) => {
  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const [isPassportOpen, setIsPassportOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newGenerate, setNewGenerate] = useState(false);
  const [qrPage, setQrPage] = useState(0);

  const handleVoyagerTicketsCompleted = () => {
    if (!voyagerTicketsData?.getTickets) {
      return '';
    }
  };

  const [getTicketsQuery, { loading: voyagerTicketsLoading, data: voyagerTicketsData }] = useLazyQuery(getTickets, {
    fetchPolicy: 'network-only',
    onCompleted: handleVoyagerTicketsCompleted
  });

  const isLoading = voyagerTicketsLoading;
  const isMultiJourney = isBookingMultiJourney(booking?.additionalDetails);

  const isGenerationPossible =
    selectedItemRefs.length > 0 &&
    runAfterSaleValidator(
      booking,
      selectedItemRefs
    )(
      isMultiJourney
        ? [allSegmentsOfService([serviceTypeCodes.GREENSPEED]), bookingIsConfirmed, allSelectedProductsConfirmedCheckedInOrPRRRefunded]
        : [allSegmentsOfService([serviceTypeCodes.EUROSTAR]), bookingIsConfirmed, allSelectedProductsConfirmedCheckedInOrPRRRefunded]
    ) &&
    runAfterSaleValidator(booking, selectedItemRefs, user, 'some')([allSegmentsCanBeRebooked, userCanOverrideAfterSalesRules]) &&
    isUserStationAgent(user);

  const isAbsOrAds = booking.outbound.concat(booking.inbound).some((leg) => leg.addOnLeg != null);
  const isExchangeDisabled = disabled || !isGenerationPossible || isAbsOrAds;

  const handleClick = async () => {
    if (isPassportRequired) {
      setIsPassportOpen(true);
      return;
    }

    if (disabled) {
      return;
    }

    setNewGenerate(true);
    setQrPage(0);
    setSelectedPlatform(null);

    const ticketNumbers = booking.inbound
      .concat(booking.outbound)
      .concat(booking.outbound.filter((l) => l.addOnLeg).map((l) => l.addOnLeg))
      .concat(booking.inbound.filter((l) => l.addOnLeg).map((l) => l.addOnLeg))
      .flat()
      .map((l) => l.products)
      .flat()
      .filter((p) => p.isCancelled === false)
      .map((p) => ({ ...p, __typename: 'BookingItem' }))
      .filter((p) => selectedItemRefs.includes(p.itemRef))
      .map((p) => p.ticketNumber);

    getTicketsQuery({
      variables: {
        bookingReference: booking.reference,
        lastName: booking.passengers[0].lastName,
        ticketNumbers: ticketNumbers
      }
    });
  };

  if (voyagerTicketsData && !showModal && newGenerate) {
    setShowModal(true);
    setNewGenerate(false);
  }

  const toggleModal = () => setShowModal(!showModal);
  const selectPlatform = (plat) => {
    setQrPage(0);
    setSelectedPlatform(plat);
  };
  const isPrevDisabled = () => qrPage === 0;
  const isNextDisabled = () => qrPage >= selectedItemRefs.length - 1;
  const increasePageBy = (no) => setQrPage(qrPage + no);

  const getQRUrl = () => {
    if (!voyagerTicketsData?.getTickets) {
      return '';
    }

    const bookingItems = booking.eurostarBookingItems.concat(booking.greenspeedBookingItems);
    const bookingItem = bookingItems.find((bi) => bi.itemRef === selectedItemRefs[qrPage]);

    const ticket = voyagerTicketsData.getTickets.passengers
      .map((p) => p.tickets)
      .flat()
      .find((t) => t.ticketNumber === bookingItem.ticketNumber);

    let url = ticket.ticketUrls.googlePassUrl;
    if (selectedPlatform === 'GOOGLE_PAY') {
      url = ticket.ticketUrls.googlePassUrl;
    } else if (selectedPlatform === 'PASSBOOK') {
      url = ticket.ticketUrls.appleWalletUrl;
    }

    return url;
  };

  const getTicketTitle = () => {
    if (!voyagerTicketsData?.getTickets) {
      return '';
    }

    const bookingItems = booking.eurostarBookingItems.concat(booking.greenspeedBookingItems);
    const bookingItem = bookingItems.find((bi) => bi.itemRef === selectedItemRefs[qrPage]);

    const ticket = voyagerTicketsData.getTickets.passengers
      .map((p) => p.tickets)
      .flat()
      .find((t) => t.ticketNumber === bookingItem.ticketNumber);

    let title = `${bookingItem.passenger.firstName} ${bookingItem.passenger.lastName} * `;
    title += `${ticket.departure.station.uicStationName} -> ${ticket.arrival.station.uicStationName} * `;
    title += `${ticket.departure.departureDate} ${ticket.departure.departureTime}`;

    return title;
  };

  const togglePassportModal = () => {
    setIsPassportOpen(!isPassportOpen);
  };

  const onCaptureTravelDocument = () => {
    history.push(getBookingTravelDocumentsURL(booking.reference, membershipNumber));
  };

  return (
    <>
      <Modal isOpen={showModal} onClose={toggleModal}>
        {!selectedPlatform && (
          <>
            <FormattedMessage id="qrCodes.selectPlatform" />

            <br />

            <ImgContainer>
              <AppleImg title="iOS" onClick={() => selectPlatform(PASSBOOK_IOS)} />
              <AndroidImg title="Android" onClick={() => selectPlatform(PASSBOOK_ANDROID)} />
            </ImgContainer>
          </>
        )}

        {selectedPlatform && (
          <>
            <CentredLabelContainerBold>{getTicketTitle()}</CentredLabelContainerBold>

            <br />

            <ImgContainer>
              <QRCode value={getQRUrl()} />
            </ImgContainer>

            <br />

            <CentredLabelContainer>{`${qrPage + 1} / ${selectedItemRefs.length}`}</CentredLabelContainer>

            <ButtonContainer>
              <NavigationButton disabled={isPrevDisabled()} onClick={() => increasePageBy(-1)}>
                <FormattedMessage id="qrCodes.prev" />
              </NavigationButton>
              &nbsp;
              <NavigationButton disabled={isNextDisabled()} onClick={() => increasePageBy(1)}>
                <FormattedMessage id="qrCodes.next" />
              </NavigationButton>
            </ButtonContainer>
          </>
        )}
      </Modal>

      <Modal isOpen={isPassportOpen} title="Error" onClose={togglePassportModal}>
        <ModalContents>
          <FormattedMessage id="issueTicket.passportRequired1" />
          <br />
          <FormattedMessage id="issueTicket.passportRequired2" />
        </ModalContents>
        <ButtonWrapper>
          <ConfirmButton variant="primary" onClick={onCaptureTravelDocument}>
            <FormattedMessage id="advancedPaxInfo.title" />
          </ConfirmButton>
        </ButtonWrapper>
      </Modal>

      <CanPerform afterSalesShortcut={GENERATE_QR_CODES}>
        {(isEligible) =>
          isEligible && (
            <CtaButton
              type="button"
              data-cy="generate-qrs-cta"
              disabled={isExchangeDisabled || voyagerTicketsLoading}
              label={label}
              loading={isLoading}
              onClick={handleClick}
            >
              {label}
            </CtaButton>
          )
        }
      </CanPerform>
    </>
  );
};

GenerateQRCodesCta.propTypes = {
  user: userShape,
  booking: bookingShape,
  label: PropTypes.element,
  disabled: PropTypes.bool,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  isPassportRequired: PropTypes.bool,
  history: historyShape
};

export default GenerateQRCodesCta;
